import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import Logo from "../../assets/images/skysalepklogo.png";
import service from "../../Service/Service";
import "./Footer.css";

const CONTACT = {
  phone: "03338820922",
  phoneDisplay: "0333 8820922",
  email: "support@skysale.pk",
  address: "32 IT tower, Link road model town, Lahore",
  whatsapp: "+92 333 8820922",
  whatsappDigits: "923338820922",
};

const LEGAL_LINKS = [
  { label: "Privacy Policy", href: "/privacypolicy" },
  { label: "Terms of Service", href: "/tc" },
];

const SOCIAL_LINKS = [
  { name: "Facebook", href: "https://facebook.com/", src: "/social/facebook.svg" },
  { name: "Instagram", href: "https://instagram.com/", src: "/social/instagram.svg" },
  { name: "X (Twitter)", href: "https://twitter.com/", src: "/social/x.svg" },
];

const PAYMENT_LOGOS = [
  { name: "Visa", file: "visa.svg" },
  { name: "Mastercard", file: "mastercard.svg" },
  { name: "American Express", file: "american-express.svg" },
  { name: "PayPal", file: "paypal.svg" },
  { name: "Apple Pay", file: "apple.svg" },
  { name: "Stripe", file: "stripe.svg" },
];

const Footer = () => {
  const navigate = useNavigate();
  const [pageDeals, setPageDeals] = useState([]);

  useEffect(() => {
    service
      .getPageDeals()
      .then((response) => {
        if (response?.data?.success && Array.isArray(response.data.deals)) {
          setPageDeals(response.data.deals);
        } else {
          setPageDeals([]);
        }
      })
      .catch(() => setPageDeals([]));
  }, []);

  const handleDealClick = (deal) => {
    if (deal.seo_slug) {
      navigate(`/deals/${deal.seo_slug}`);
      return;
    }

    const bookingData = {
      departure: deal.origin_code,
      arrival: deal.destination_code,
      departure_name: `${deal.origin_city} (${deal.origin_code})`,
      arrival_name: `${deal.destination_city} (${deal.destination_code})`,
      date: deal.departure_date || "",
      return_date: deal.return_date || "",
      adults: "1",
      children: "0",
      infants: "0",
      flight_type: deal.return_date ? "return" : "one-way",
      flight_class: "economy",
    };

    localStorage.setItem("booking", JSON.stringify(bookingData));
    localStorage.removeItem("offer");
    navigate("/flight-detail-search?package=0");
  };

  return (
    <>
      <footer className="sf-footer">
        {/* Top accent */}
        <div className="sf-footer-accent" />

        {/* Help bar */}
        <div className="sf-footer-help">
          <div className="sf-footer-help-inner">
            <p className="sf-footer-help-text">Need help planning your trip?</p>
            <a href={`tel:${CONTACT.phone}`} className="sf-footer-call-btn">
              <Phone />
              Call us now
            </a>
          </div>
        </div>

        {/* Main Footer */}
        <div className="sf-footer-main">
          <div className="sf-footer-grid">
            {/* Brand Column */}
            <div>
              <Link to="/" className="sf-footer-logo">
                <img src={Logo} alt="SKYSALE PK" />
              </Link>
              <p className="sf-footer-desc">
                Find the best flights and Holiday packages tailored for you. Book with confidence.
              </p>

              <p className="sf-footer-label">Follow Us</p>
              <div className="sf-footer-social">
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sf-footer-social-link"
                    aria-label={social.name}
                  >
                    <img src={social.src} alt={social.name} className="sf-footer-social-img" />
                  </a>
                ))}
              </div>

              <div>
                <p className="sf-footer-label">Trusted & Accredited</p>
                <div className="sf-footer-badges">
                  <div className="sf-footer-badge">
                    <img src="/badges/atol.svg" alt="ATOL Protected" />
                    <span className="sf-footer-badge-tag green">Protected</span>
                  </div>
                  <div className="sf-footer-badge">
                    <img src="/badges/iata.svg" alt="IATA Member" />
                    <span className="sf-footer-badge-tag blue">Accredited</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links — same as Popular Cheap Flight Routes */}
            <div>
              <h4 className="sf-footer-col-title">Quick Links</h4>
              <div className="sf-footer-col-rule" />
              <ul className="sf-footer-list">
                {pageDeals.length > 0 ? (
                  pageDeals.map((deal, index) => (
                    <li key={deal.id || `${deal.origin_code}-${deal.destination_code}-${index}`}>
                      <button
                        type="button"
                        className="sf-footer-link-btn"
                        onClick={() => handleDealClick(deal)}
                      >
                        <span className="sf-footer-dot" />
                        Cheap flights from {deal.origin_city} to {deal.destination_city}
                      </button>
                    </li>
                  ))
                ) : (
                  <li>
                    <Link to="/">
                      <span className="sf-footer-dot" />
                      Search flights
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="sf-footer-col-title">Services</h4>
              <div className="sf-footer-col-rule" />
              <ul className="sf-footer-list">
                <li>
                  <Link to="/">
                    <span className="sf-footer-dot" />
                    Flight Search
                  </Link>
                </li>
                <li>
                  <Link to="/packages">
                    <span className="sf-footer-dot" />
                    Packages
                  </Link>
                </li>
                <li>
                  <Link to="/allpackages?category=0&type=umrah">
                    <span className="sf-footer-dot" />
                    Umrah Packages
                  </Link>
                </li>
                <li>
                  <a href={`tel:${CONTACT.phone}`}>
                    <span className="sf-footer-dot" />
                    Group Bookings
                  </a>
                </li>
                <li>
                  <a href={`tel:${CONTACT.phone}`}>
                    <span className="sf-footer-dot" />
                    Travel Consultation
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="sf-footer-col-title">Contact Us</h4>
              <div className="sf-footer-col-rule" />
              <ul className="sf-footer-contact-list">
                <li>
                  <a href={`tel:${CONTACT.phone}`}>
                    <div className="sf-footer-icon blue">
                      <Phone />
                    </div>
                    <div>
                      <p className="sf-footer-contact-label">Call us</p>
                      <span className="sf-footer-contact-value blue">{CONTACT.phoneDisplay}</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${CONTACT.email}`}>
                    <div className="sf-footer-icon orange">
                      <Mail />
                    </div>
                    <div>
                      <p className="sf-footer-contact-label">Email us</p>
                      <span className="sf-footer-contact-value orange">{CONTACT.email}</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a
                    href={`https://wa.me/${CONTACT.whatsappDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="sf-footer-icon green">
                      <MessageCircle />
                    </div>
                    <div>
                      <p className="sf-footer-contact-label">WhatsApp</p>
                      <span className="sf-footer-contact-value green">{CONTACT.whatsapp}</span>
                    </div>
                  </a>
                </li>
                <li className="sf-footer-contact-item">
                  <div className="sf-footer-icon gray">
                    <MapPin />
                  </div>
                  <div>
                    <p className="sf-footer-contact-label">Office</p>
                    <span className="sf-footer-contact-value muted">{CONTACT.address}</span>
                  </div>
                </li>
              </ul>

              <div className="sf-footer-company">
                <p className="sf-footer-company-name">BUDGET TRAVEL LIMITED</p>
                <p className="sf-footer-company-number">Company number 05976009</p>
              </div>

              <div className="sf-footer-map">
                <div className="sf-footer-map-bar">
                  <div className="sf-footer-map-bar-left">
                    <div className="sf-footer-map-pin">
                      <MapPin />
                    </div>
                    <span className="sf-footer-map-title">Our Office</span>
                  </div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sf-footer-map-link"
                  >
                    Open in Maps ↗
                  </a>
                </div>
                <iframe
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.address)}&output=embed&z=15`}
                  title="Office location"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="sf-footer-map-address">
                  <p>{CONTACT.address}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods Strip */}
        <div className="sf-footer-payments">
          <div className="sf-footer-payments-inner">
            <div className="sf-footer-secure">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              <span>Secure payments accepted</span>
            </div>
            <div className="sf-footer-pay-logos">
              {PAYMENT_LOGOS.map(({ name, file }) => (
                <img
                  key={file}
                  src={`/badges/payments/${file}`}
                  alt={name}
                  className="sf-footer-pay-logo"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="sf-footer-bottom">
          <div className="sf-footer-bottom-inner">
            <p className="sf-footer-copy">© SKYSALE PK. All rights reserved.</p>
            <div className="sf-footer-legal">
              {LEGAL_LINKS.map((link) => (
                <Link key={link.href} to={link.href}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
